/**
 * Wizard CRM para criação/edição de propostas B2B (3 passos):
 *   1. Briefing — info do cliente + descrição do desafio
 *   2. Conteúdo — título, diagnóstico, testemunho a usar
 *   3. Investimento — produtos do catálogo + descontos + validade → guarda
 *
 * Backbone P0.2: rascunho only (não envia, não renderiza).
 * Próxima iteração: render HTML público + PDF + envio + IA.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight, FloppyDisk, Plus, Trash, SpinnerGap, CheckCircle } from "@phosphor-icons/react";

const fmt = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);

export default function CRMPropostaWizardPage() {
  const navigate = useNavigate();
  const { id: idProposta } = useParams();
  const [searchParams] = useSearchParams();
  const idNegocio = searchParams.get("negocio");

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  // Form state
  const [form, setForm] = useState({
    nome_cliente_empresa: "",
    sector: "",
    titulo_programa: "",
    diagnostico: "",
    texto_capa: "",
    briefing: "",
    valida_dias: 60,
    desconto_pct: 0,
    testemunho_id_selecionado: null,
    id_negocio: idNegocio ? parseInt(idNegocio, 10) : null,
    produtos: [],
  });

  // Catálogo + testemunhos + preçário 2026
  const [catalogo, setCatalogo] = useState([]);
  const [testemunhos, setTestemunhos] = useState([]);
  const [precario, setPrecario] = useState(null);
  const [catalogFilter, setCatalogFilter] = useState("");
  const [proposta, setProposta] = useState(null); // após guardar

  // Carregar catálogo + testemunhos + preçário B2B activo
  useEffect(() => {
    api.get("/propostas/catalogo-b2b").then((r) => setCatalogo(r?.data?.items || [])).catch(() => {});
    api.get("/propostas/testemunhos").then((r) => setTestemunhos(r?.data?.items || [])).catch(() => {});
    api.get("/precarios/active").then((r) => setPrecario(r?.data?.item || null)).catch(() => {});
  }, []);

  // Se idProposta vem na URL, carregar para edição
  useEffect(() => {
    if (!idProposta) return;
    setLoading(true);
    api.get(`/propostas/${idProposta}`)
      .then((r) => {
        const it = r?.data?.item;
        if (!it) return;
        setProposta(it);
        setForm({
          nome_cliente_empresa: it.NomeClienteEmpresa || "",
          sector: it.Sector || "",
          titulo_programa: it.TituloPrograma || "",
          diagnostico: it.Diagnostico || "",
          texto_capa: it.TextoCapa || "",
          briefing: it.Briefing || "",
          valida_dias: 60,
          desconto_pct: parseFloat(it.DescontoPct || 0),
          testemunho_id_selecionado: it.TestemunhoIDSelecionado || null,
          id_negocio: it.ID_Negocio || null,
          produtos: (it.Produtos || []).map((p) => ({
            id_produto: p.ID_Produto,
            quantidade: p.Quantidade,
            preco_unitario: parseFloat(p.PrecoUnitario),
            desconto_pct: parseFloat(p.DescontoPct),
            descricao_override: p.DescricaoOverride || "",
            isento_iva: (p.IsentoIVA || "N").toUpperCase(),
            _desc: p.DescricaoProduto,
          })),
        });
      })
      .catch((e) => setError(e?.response?.data?.detail || "Erro a carregar."))
      .finally(() => setLoading(false));
  }, [idProposta]);

  // IVA do preçário activo (default 23). Cursos isentos vêm com isento_iva='S'.
  const ivaPct = useMemo(() => Number(precario?.IVA_Pct ?? 23), [precario]);

  const totals = useMemo(() => {
    const lines = (form.produtos || []).map((p) => {
      const liquido = (p.preco_unitario || 0) * (p.quantidade || 1) * (1 - (p.desconto_pct || 0) / 100);
      const isento = (p.isento_iva || "N").toUpperCase() === "S";
      return { liquido, isento };
    });
    const subtotalSemIVA = lines.reduce((s, l) => s + l.liquido, 0);
    // Aplicar desconto global proporcionalmente a cada linha
    const descG = (form.desconto_pct || 0) / 100;
    const baseIsenta = lines.filter((l) => l.isento).reduce((s, l) => s + l.liquido * (1 - descG), 0);
    const baseSujeita = lines.filter((l) => !l.isento).reduce((s, l) => s + l.liquido * (1 - descG), 0);
    const ivaValor = baseSujeita * (ivaPct / 100);
    const total = baseIsenta + baseSujeita + ivaValor;
    return {
      subtotal: subtotalSemIVA,
      subtotalAposDesconto: baseIsenta + baseSujeita,
      baseIsenta,
      baseSujeita,
      ivaValor,
      ivaPct,
      total,
    };
  }, [form.produtos, form.desconto_pct, ivaPct]);

  const filteredCatalogo = useMemo(() => {
    const q = catalogFilter.trim().toLowerCase();
    if (!q) return catalogo;
    return catalogo.filter((x) => (x.Descricao || "").toLowerCase().includes(q));
  }, [catalogo, catalogFilter]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addProduto = (p) => {
    setForm((f) => ({
      ...f,
      produtos: [...f.produtos, {
        id_produto: p.ID_Produto,
        quantidade: 1,
        preco_unitario: parseFloat(p.PrecoBase || 0),
        desconto_pct: 0,
        isento_iva: "N",
        _desc: p.Descricao,
      }],
    }));
  };

  const removeProduto = (idx) => {
    setForm((f) => ({ ...f, produtos: f.produtos.filter((_, i) => i !== idx) }));
  };

  const setProdutoField = (idx, k, v) => {
    setForm((f) => ({
      ...f,
      produtos: f.produtos.map((p, i) => i === idx ? { ...p, [k]: v } : p),
    }));
  };

  const save = async (goToList = false) => {
    setError(""); setSaving(true);
    try {
      const payload = {
        nome_cliente_empresa: form.nome_cliente_empresa,
        sector: form.sector || null,
        titulo_programa: form.titulo_programa || null,
        diagnostico: form.diagnostico || null,
        texto_capa: form.texto_capa || null,
        briefing: form.briefing || null,
        desconto_pct: parseFloat(form.desconto_pct) || 0,
        testemunho_id_selecionado: form.testemunho_id_selecionado || null,
        id_negocio: form.id_negocio || null,
        valida_dias: parseInt(form.valida_dias, 10) || 60,
        produtos: form.produtos.map((p) => ({
          id_produto: p.id_produto,
          quantidade: parseInt(p.quantidade, 10) || 1,
          preco_unitario: parseFloat(p.preco_unitario) || 0,
          desconto_pct: parseFloat(p.desconto_pct) || 0,
          descricao_override: p.descricao_override || null,
          isento_iva: (p.isento_iva || "N").toUpperCase(),
        })),
      };
      let id = idProposta;
      if (id) {
        await api.patch(`/propostas/${id}`, payload);
      } else {
        const r = await api.post(`/propostas`, payload);
        id = r?.data?.id_proposta;
        if (id && !goToList) navigate(`/propostas/${id}`, { replace: true });
      }
      setSavedAt(new Date());
      if (goToList) navigate("/propostas");
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro a guardar proposta.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="proposta-loading">
      <SpinnerGap size={48} className="animate-spin text-[#0F1829]" />
    </div>;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8 px-4 sm:px-6 lg:px-8" data-testid="proposta-wizard">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <button onClick={() => navigate("/propostas")} className="text-sm text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1 mb-2"
              data-testid="proposta-back-btn">
              <ArrowLeft size={14} /> Voltar à lista
            </button>
            <h1 className="text-3xl font-black tracking-tighter text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
              {idProposta ? `Proposta ${proposta?.Referencia || ''}` : 'Nova Proposta'}
            </h1>
            {proposta && <p className="text-xs text-[#6B7280] mt-1">Estado: <span className="font-semibold uppercase">{proposta.Estado}</span></p>}
          </div>
          {savedAt && <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle size={14} weight="fill"/> Guardado às {savedAt.toLocaleTimeString("pt-PT")}</span>}
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <button onClick={() => setStep(s)} data-testid={`proposta-step-${s}`}
                className={`flex-1 h-1.5 rounded-full transition-colors ${step >= s ? "bg-[#0F1829]" : "bg-[#E5E7EB]"}`} />
            </div>
          ))}
        </div>
        <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-4">
          Passo {step} de 3 · {step === 1 ? "Briefing" : step === 2 ? "Conteúdo" : "Investimento"}
        </p>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-md p-3 mb-4" data-testid="proposta-error">{error}</div>}

        {/* STEP 1: Briefing */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-4" data-testid="proposta-step1">
            <Field label="Nome da empresa cliente *" required>
              <input type="text" value={form.nome_cliente_empresa} onChange={(e) => setField("nome_cliente_empresa", e.target.value)}
                data-testid="proposta-nome-empresa"
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" placeholder="Ex.: Grupo Fábrica dos Óculos" />
            </Field>
            <Field label="Sector">
              <input type="text" value={form.sector} onChange={(e) => setField("sector", e.target.value)}
                data-testid="proposta-sector"
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" placeholder="Ex.: Optica, Restauracao, Industria" />
            </Field>
            <Field label="Briefing — desafio do cliente"
              hint="Notas internas. A IA vai usar este texto para sugerir programa e produtos (próxima iteração).">
              <textarea value={form.briefing} onChange={(e) => setField("briefing", e.target.value)}
                data-testid="proposta-briefing" rows={6}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm resize-y" placeholder="Cliente pretende coaching para 12 lojas, equipa de vendas com 50 colaboradores..." />
            </Field>
          </div>
        )}

        {/* STEP 2: Conteúdo */}
        {step === 2 && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-4" data-testid="proposta-step2">
            <Field label="Título do programa">
              <input type="text" value={form.titulo_programa} onChange={(e) => setField("titulo_programa", e.target.value)}
                data-testid="proposta-titulo"
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" placeholder="Ex.: Programa de Excelência 2026" />
            </Field>
            <Field label="Texto da capa" hint="Subtítulo curto a aparecer na primeira página.">
              <input type="text" value={form.texto_capa} onChange={(e) => setField("texto_capa", e.target.value)}
                data-testid="proposta-textocapa" maxLength={200}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" />
            </Field>
            <Field label="Diagnóstico" hint="3-4 frases que reconheçam o desafio do cliente.">
              <textarea value={form.diagnostico} onChange={(e) => setField("diagnostico", e.target.value)}
                data-testid="proposta-diagnostico" rows={4}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm resize-y" />
            </Field>
            <Field label="Testemunho a destacar">
              <select value={form.testemunho_id_selecionado || ""} onChange={(e) => setField("testemunho_id_selecionado", e.target.value ? parseInt(e.target.value, 10) : null)}
                data-testid="proposta-testemunho"
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm">
                <option value="">— Sem destaque —</option>
                {testemunhos.map((t) => (
                  <option key={t.ID_Testemunho} value={t.ID_Testemunho}>
                    {t.ClienteEmpresa} — {t.Autor} ({t.Sectores})
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {/* STEP 3: Investimento */}
        {step === 3 && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-5" data-testid="proposta-step3">
            {precario && precario.Items?.length > 0 && (
              <div data-testid="proposta-precario-section">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold text-[#0F1829]">Adicionar do Preçário {precario.Ano}</h3>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#0F1829] text-white rounded">{precario.Items.length} items · IVA {Number(precario.IVA_Pct).toFixed(0)}%</span>
                </div>
                <div className="max-h-56 overflow-y-auto border border-[#F3F4F6] rounded-md divide-y divide-[#F3F4F6]">
                  {precario.Items.map((it) => (
                    <button key={it.ID_PrecarioItem} type="button"
                      data-testid={`proposta-add-precario-${it.ID_PrecarioItem}`}
                      onClick={() => setForm((f) => ({
                        ...f,
                        produtos: [...f.produtos, {
                          id_produto: it.ID_Produto || 0,
                          quantidade: 1,
                          preco_unitario: parseFloat(it.ValorUnitario || 0),
                          desconto_pct: 0,
                          isento_iva: (it.IsentoIVA || "N").toUpperCase(),
                          _desc: `${it.Descricao}${it.DuracaoTexto ? ` (${it.DuracaoTexto})` : ""}`,
                          descricao_override: it.Descricao,
                        }],
                      }))}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-[#F9FAFB] flex items-center justify-between gap-2">
                      <span className="font-mono text-[10px] text-[#9CA3AF] w-14 shrink-0">{it.Categoria}</span>
                      <span className="flex-1 truncate">
                        {it.Descricao}
                        {it.DuracaoTexto && <span className="ml-1 text-[10px] text-[#6B7280]">· {it.DuracaoTexto}</span>}
                        {it.Trainer && <span className="ml-1 text-[10px] px-1 py-0.5 bg-[#0F1829] text-white rounded">{it.Trainer}</span>}
                      </span>
                      <span className="text-[#6B7280] tabular-nums">{fmt(it.ValorUnitario)}</span>
                      <Plus size={14} className="text-[#0F1829] shrink-0" />
                    </button>
                  ))}
                </div>
                {precario.Descontos?.length > 0 && (
                  <div className="mt-2 text-[11px] text-[#6B7280]">
                    <span className="font-bold uppercase tracking-wider mr-1">Descontos do preçário:</span>
                    {precario.Descontos.map((d, i) => (
                      <span key={d.ID_PrecarioDesconto} className="ml-1">
                        {i > 0 && "· "}
                        {d.Descricao}: <strong>{Number(d.DescontoPct).toFixed(2)}%</strong>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <h3 className="text-base font-semibold text-[#0F1829] mb-2">Adicionar do catálogo</h3>
              <input type="text" value={catalogFilter} onChange={(e) => setCatalogFilter(e.target.value)}
                data-testid="proposta-catalog-filter" placeholder="Pesquisar produto..."
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm mb-3" />
              <div className="max-h-56 overflow-y-auto border border-[#F3F4F6] rounded-md divide-y divide-[#F3F4F6]">
                {filteredCatalogo.slice(0, 50).map((p) => (
                  <button key={p.ID_Produto} onClick={() => addProduto(p)} type="button"
                    data-testid={`proposta-add-${p.ID_Produto}`}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-[#F9FAFB] flex items-center justify-between gap-2">
                    <span className="flex-1 truncate">{p.Descricao}</span>
                    <span className="text-[#6B7280] tabular-nums">{fmt(p.PrecoBase)}</span>
                    <Plus size={14} className="text-[#0F1829] shrink-0" />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-[#0F1829] mb-2">Linhas da proposta</h3>
              {form.produtos.length === 0 ? (
                <p className="text-xs text-[#9CA3AF] italic">Sem produtos. Adicione do catálogo acima.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="proposta-linhas-tabela">
                    <thead className="bg-[#F9FAFB] text-left">
                      <tr>
                        <th className="px-2 py-1.5">Produto</th>
                        <th className="px-2 py-1.5 w-16">Qtd</th>
                        <th className="px-2 py-1.5 w-28">Preço un.</th>
                        <th className="px-2 py-1.5 w-20">Desc %</th>
                        <th className="px-2 py-1.5 w-20">IVA</th>
                        <th className="px-2 py-1.5 w-28 text-right">Total s/IVA</th>
                        <th className="px-2 py-1.5 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.produtos.map((p, i) => {
                        const isento = (p.isento_iva || "N").toUpperCase() === "S";
                        const liquido = (p.preco_unitario || 0) * (p.quantidade || 1) * (1 - (p.desconto_pct || 0) / 100);
                        return (
                          <tr key={i} className="border-t border-[#F3F4F6]" data-testid={`proposta-linha-${i}`}>
                            <td className="px-2 py-1.5">{p._desc || `Produto #${p.id_produto}`}</td>
                            <td className="px-2 py-1.5"><input type="number" min="1" value={p.quantidade} onChange={(e) => setProdutoField(i, "quantidade", e.target.value)} className="w-full px-1 py-0.5 border border-[#E5E7EB] rounded text-xs" /></td>
                            <td className="px-2 py-1.5"><input type="number" step="0.01" value={p.preco_unitario} onChange={(e) => setProdutoField(i, "preco_unitario", e.target.value)} className="w-full px-1 py-0.5 border border-[#E5E7EB] rounded text-xs tabular-nums" /></td>
                            <td className="px-2 py-1.5"><input type="number" min="0" max="100" value={p.desconto_pct} onChange={(e) => setProdutoField(i, "desconto_pct", e.target.value)} className="w-full px-1 py-0.5 border border-[#E5E7EB] rounded text-xs" /></td>
                            <td className="px-2 py-1.5">
                              <select value={p.isento_iva || "N"} onChange={(e) => setProdutoField(i, "isento_iva", e.target.value)}
                                data-testid={`proposta-linha-iva-${i}`}
                                className="w-full px-1 py-0.5 border border-[#E5E7EB] rounded text-xs">
                                <option value="N">+{ivaPct.toFixed(0)}%</option>
                                <option value="S">Isento</option>
                              </select>
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {fmt(liquido)}
                              {!isento && <div className="text-[10px] text-[#9CA3AF]">c/ IVA: {fmt(liquido * (1 + ivaPct / 100))}</div>}
                            </td>
                            <td className="px-2 py-1.5"><button onClick={() => removeProduto(i)} type="button" data-testid={`proposta-remove-${i}`} className="text-red-500 hover:text-red-700"><Trash size={14} /></button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#E5E7EB]">
              <Field label="Desconto global (%)">
                <input type="number" min="0" max="100" step="0.5" value={form.desconto_pct} onChange={(e) => setField("desconto_pct", e.target.value)}
                  data-testid="proposta-desconto-global"
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" />
              </Field>
              <Field label="Validade (dias)">
                <input type="number" min="1" value={form.valida_dias} onChange={(e) => setField("valida_dias", e.target.value)}
                  data-testid="proposta-validade"
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" />
              </Field>
            </div>

            <div className="bg-[#F9FAFB] rounded-md p-4 border border-[#E5E7EB]" data-testid="proposta-totais">
              <div className="space-y-1 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-[#6B7280]">Subtotal (s/ IVA, antes do desconto global)</span>
                  <span className="font-mono tabular-nums text-[#0F1829]" data-testid="proposta-subtotal">{fmt(totals.subtotal)}</span>
                </div>
                {Number(form.desconto_pct) > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#6B7280]">Desconto global aplicado ({Number(form.desconto_pct).toFixed(2)}%)</span>
                    <span className="font-mono tabular-nums text-red-600">−{fmt(totals.subtotal - totals.subtotalAposDesconto)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-[#E5E7EB] pt-1 mt-1">
                  <span className="text-[#6B7280]">Base isenta de IVA (cursos)</span>
                  <span className="font-mono tabular-nums text-[#0F1829]" data-testid="proposta-base-isenta">{fmt(totals.baseIsenta)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#6B7280]">Base sujeita a IVA</span>
                  <span className="font-mono tabular-nums text-[#0F1829]" data-testid="proposta-base-sujeita">{fmt(totals.baseSujeita)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#6B7280]">IVA ({totals.ivaPct.toFixed(0)}%)</span>
                  <span className="font-mono tabular-nums text-[#0F1829]" data-testid="proposta-iva">+{fmt(totals.ivaValor)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between border-t-2 border-[#0F1829] pt-2 mt-2">
                <span className="text-sm font-bold uppercase tracking-wider text-[#0F1829]">Total c/ IVA</span>
                <span className="text-2xl font-black tabular-nums text-[#0F1829]" data-testid="proposta-total">{fmt(totals.total)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-2">
            {step > 1 && (
              <button onClick={() => setStep((s) => s - 1)} type="button" data-testid="proposta-prev"
                className="px-4 py-2 text-sm border-2 border-[#0F1829] text-[#0F1829] rounded-md hover:bg-[#F3F4F6] inline-flex items-center gap-2">
                <ArrowLeft size={16} /> Anterior
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => save(false)} disabled={saving || !form.nome_cliente_empresa} type="button"
              data-testid="proposta-save-btn"
              className="px-4 py-2 text-sm bg-white border-2 border-[#0F1829] text-[#0F1829] rounded-md hover:bg-[#F3F4F6] disabled:opacity-50 inline-flex items-center gap-2">
              {saving ? <SpinnerGap size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
              Guardar rascunho
            </button>
            {step < 3 ? (
              <button onClick={() => setStep((s) => s + 1)} type="button" disabled={!form.nome_cliente_empresa}
                data-testid="proposta-next"
                className="px-4 py-2 text-sm bg-[#0F1829] text-white rounded-md hover:bg-[#001f6e] disabled:opacity-50 inline-flex items-center gap-2">
                Seguinte <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={() => save(true)} disabled={saving || !form.nome_cliente_empresa} type="button"
                data-testid="proposta-save-go-btn"
                className="px-4 py-2 text-sm bg-[#0F1829] text-white rounded-md hover:bg-[#001f6e] disabled:opacity-50 inline-flex items-center gap-2">
                {saving ? <SpinnerGap size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
                Guardar e fechar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#374151] mb-1.5">
        {label}{required && <span className="text-red-500"> *</span>}
      </span>
      {children}
      {hint && <p className="text-[10px] text-[#9CA3AF] mt-1">{hint}</p>}
    </label>
  );
}
